import { useState, useEffect, useRef } from 'react';
import type { 
  WebVitalsMetric, 
  MetricName, 
  MetricRating
} from '@/services/webVitals';
import { 
  subscribeToMetrics,
  DEFAULT_THRESHOLDS
} from '@/services/webVitals';

// Metric history entry
export interface MetricHistoryEntry {
  metric: WebVitalsMetric;
  timestamp: number;
}

// Current metrics state
export interface WebVitalsMetrics {
  CLS?: WebVitalsMetric;
  FCP?: WebVitalsMetric;
  LCP?: WebVitalsMetric;
  TTFB?: WebVitalsMetric;
  INP?: WebVitalsMetric;
}

// Overall rating based on all metrics
export interface OverallRating {
  rating: MetricRating;
  score: number; // 0-100
  details: {
    good: number;
    needsImprovement: number;
    poor: number;
  };
}

// Hook result interface
export interface UseWebVitalsResult {
  metrics: WebVitalsMetrics;
  history: MetricHistoryEntry[];
  isCollecting: boolean;
  overallRating: OverallRating;
  clearHistory: () => void;
  getMetricHistory: (name: MetricName) => MetricHistoryEntry[];
}

// Maximum history entries to keep
const MAX_HISTORY_SIZE = 50;

export function useWebVitals(): UseWebVitalsResult {
  const [metrics, setMetrics] = useState<WebVitalsMetrics>({});
  const [history, setHistory] = useState<MetricHistoryEntry[]>([]);
  const [isCollecting, setIsCollecting] = useState(false);
  const historyRef = useRef<MetricHistoryEntry[]>([]);

  useEffect(() => {
    setIsCollecting(true);

    // Subscribe to metric updates
    const unsubscribe = subscribeToMetrics((metric: WebVitalsMetric) => {
      // Update current metrics
      setMetrics(prev => ({
        ...prev,
        [metric.name]: metric,
      }));

      // Add to history
      const historyEntry: MetricHistoryEntry = {
        metric,
        timestamp: metric.timestamp,
      };

      historyRef.current = [...historyRef.current, historyEntry].slice(-MAX_HISTORY_SIZE);
      setHistory(historyRef.current);
    });

    return () => {
      unsubscribe();
      setIsCollecting(false);
    };
  }, []);

  // Calculate overall rating
  const overallRating: OverallRating = (() => {
    const collectedMetrics = Object.values(metrics);
    
    if (collectedMetrics.length === 0) {
      return {
        rating: 'good',
        score: 100,
        details: { good: 0, needsImprovement: 0, poor: 0 },
      };
    }

    const details = collectedMetrics.reduce(
      (acc, metric) => {
        acc[metric.rating]++;
        return acc;
      },
      { good: 0, 'needs-improvement': 0, poor: 0 } as Record<MetricRating, number>
    );

    // Calculate score (0-100)
    const totalMetrics = collectedMetrics.length;
    const score = Math.round(
      ((details.good * 100) + 
       (details['needs-improvement'] * 50) + 
       (details.poor * 0)) / totalMetrics
    );

    // Determine overall rating
    let rating: MetricRating;
    if (details.poor > 0) {
      rating = 'poor';
    } else if (details['needs-improvement'] > 0) {
      rating = 'needs-improvement';
    } else {
      rating = 'good';
    }

    return {
      rating,
      score,
      details: {
        good: details.good,
        needsImprovement: details['needs-improvement'],
        poor: details.poor,
      },
    };
  })();

  // Clear history
  const clearHistory = () => {
    historyRef.current = [];
    setHistory([]);
  };

  // Get history for specific metric
  const getMetricHistory = (name: MetricName): MetricHistoryEntry[] => {
    return history.filter(entry => entry.metric.name === name);
  };

  return {
    metrics,
    history,
    isCollecting,
    overallRating,
    clearHistory,
    getMetricHistory,
  };
}

// Helper hook to get specific metric
export function useWebVital(name: MetricName): WebVitalsMetric | undefined {
  const { metrics } = useWebVitals();
  return metrics[name];
}

// Helper to format metric value for display
export function formatMetricValue(name: MetricName, value: number): string {
  if (name === 'CLS') {
    return value.toFixed(3);
  }
  return `${Math.round(value)}ms`;
}

// Helper to get metric description
export function getMetricDescription(name: MetricName): string {
  const descriptions: Record<MetricName, string> = {
    LCP: 'Largest Contentful Paint - Loading performance',
    FCP: 'First Contentful Paint - First visual feedback',
    CLS: 'Cumulative Layout Shift - Visual stability',
    TTFB: 'Time to First Byte - Server response time',
    INP: 'Interaction to Next Paint - Responsiveness',
  };
  return descriptions[name] || name;
}

// Helper to get threshold info
export function getMetricThreshold(name: MetricName) {
  return DEFAULT_THRESHOLDS[name];
}